import { inject, Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { HttpClient, HttpHeaders, HttpResponse, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, catchError } from "rxjs/operators";
import { TranslateService } from '@ngx-translate/core';
//import 'rxjs/Rx';
//import 'rxjs/add/observable/throw';

import { ChartConfigBase } from '../models/chart-config-base';
import { HighChartDataBase } from '../models/high-chart-data-base';
import { BoxPlotHighChartData } from "../models/boxplot-high-chart-data";
import { ColumnHighChartData } from '../models/column-high-chart-data';
import { ColumnRangeHighChartData } from '../models/columnrange-high-chart-data';
import { DateTimeHighChartData } from '../models/datetime-high-chart-data';
import { FunnelHighChartData } from '../models/funnel-high-chart-data';
import { LineHighChartData } from '../models/line-high-chart-data';
import { LineCategoryHighChartData } from '../models/line-category-high-chart-data';
import { PieHighChartData } from '../models/pie-high-chart-data';
import { PolarHighChartData } from "../models/polar-high-chart-data";
import { ClickNotifier } from '../models/click-notifier';



@Injectable({ providedIn: 'root' })
export class XmlReportDataService {
    private readonly http = inject(HttpClient);
    private readonly translate = inject(TranslateService);

    public currentGroupings = new BehaviorSubject<string[]>(null);


    public getExcelTable(config: ChartConfigBase): Observable<Blob> {
        let configParams: HttpParams = this.getConfigParams(config);
        return this.http.post(config.tableUrl, configParams, {
                headers: { 
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Accept-Language': this.translate.getCurrentLang()
                },
                responseType: 'blob'
            })
            .pipe(map((response) => {
                return response;
            }));
        }

    public getHighChartData(config: ChartConfigBase, clickNotifier?: ClickNotifier): Observable<HighChartDataBase> {
        try {
            //this.setHighChartDefaults();
            let configParams: HttpParams = this.getConfigParams(config);
            const httpOptions = {
                headers: new HttpHeaders({
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Accept-Language': this.translate.getCurrentLang()
                })
            };
            return this.http.post(config.highChartUrl, configParams, httpOptions)
                .pipe(map((response: any) => {
                    switch (response.ChartType) {
                        case "column":
                        case "stacked":
                            let columnData = new ColumnHighChartData(this.translate, response, config.chartWidth, config.chartHeight, clickNotifier);
                            this.currentGroupings.next(columnData.groupings);
                            return columnData;
                        case "boxplot":
                            let boxplotData = new BoxPlotHighChartData(this.translate, response, config.chartWidth, config.chartHeight, clickNotifier);
                            this.currentGroupings.next(boxplotData.groupings);
                            return boxplotData;
                        case "datetime":
                            let dateTimeData = new DateTimeHighChartData(this.translate, response, config.chartWidth, config.chartHeight, clickNotifier);
                            this.currentGroupings.next(dateTimeData.groupings);
                            return dateTimeData;
                        case "funnel":
                        case "spline":
                            response.ChartType = "funnel";
                            let funnelData = new FunnelHighChartData(this.translate, response, config.chartWidth, config.chartHeight);
                            this.currentGroupings.next(funnelData.groupings);
                            return funnelData;
                        case "lineCategory":
                            let lineCategoryData = new LineCategoryHighChartData(this.translate, response, config.chartWidth, config.chartHeight, clickNotifier);
                            this.currentGroupings.next(lineCategoryData.groupings);
                            return lineCategoryData;
                        case "line":
                        case "point":
                            let lineData = new LineHighChartData(this.translate, response, config.chartWidth, config.chartHeight);
                            this.currentGroupings.next(lineData.groupings);
                            return lineData;
                        case "pie":
                            let pieData = new PieHighChartData(this.translate, response, config.chartWidth, config.chartHeight);
                            this.currentGroupings.next(pieData.groupings);
                            return pieData;
                        case "rangecolumn":
                            let columnRangeData = new ColumnRangeHighChartData(this.translate, response, config.chartWidth, config.chartHeight, clickNotifier);
                            this.currentGroupings.next(columnRangeData.groupings);
                            return columnRangeData;
                        case "radar":
                            let polarData = new PolarHighChartData(this.translate, response, config.chartWidth, config.chartHeight, clickNotifier);
                            this.currentGroupings.next(polarData.groupings);
                            return polarData;
                        default:
                            this.translate.get('xml-report.chart.not-implemented-msg').subscribe((result: string) => {
                                throw (result);
                            });
                        }
                        return null;
                    }))
                    .pipe(catchError(err => this.handleServerApiError(err)));

        } catch (e) {
            return throwError(() => new Error(e.Message));
        }
    }

    private getConfigParams(config: ChartConfigBase): HttpParams {
        let params = new HttpParams();
        Object.keys(config).map((key: string) => {
            //Hämta alla egenskaper på config och sätt som params i requesten till backend
            if (config[key]) {
                if (config[key].constructor === Array) {
                    let arr = config[key] as any[];
                    arr.forEach((value, index) => {
                        params = params.set(key + `[${index}]`, value.toString());
                    });
                } else {
                    let value = config[key].toString();
                    params = params.set(key, value);
                }
            }
        });
        return params;
    }

    protected handleServerApiError(errorResponse: HttpResponse<any>): Observable<HighChartDataBase> {
        return throwError(() => new Error(errorResponse['error']));
    }
}
